import React, { useState, useEffect } from 'react';
import { getCards, addCard, deleteCard, getPersons } from '../api';
import { usePagination } from '../utils';

function Cards() {
  const [cards, setCards] = useState([]);
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    card_number: '',
    bank_name: '',
    person_id: '',
    description: '',
  });
  const cardsPagination = usePagination(cards);

  const renderPaginationControls = (pagination) => {
    if (pagination.totalItems === 0) {
      return null;
    }

    return (
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between border-top px-3 py-3 gap-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">تعداد در صفحه</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pagination.pageSize}
            onChange={(e) => pagination.setPageSize(Number(e.target.value))}
          >
            {pagination.pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-muted small">
            نمایش {pagination.rangeStart} تا {pagination.rangeEnd} از {pagination.totalItems}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.prevPage}
            disabled={!pagination.canGoPrev}
          >
            قبلی
          </button>
          <span className="text-muted small">
            صفحه {pagination.currentPage} از {pagination.totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.nextPage}
            disabled={!pagination.canGoNext}
          >
            بعدی
          </button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [cardsRes, personsRes] = await Promise.all([getCards(), getPersons()]);
      setCards(cardsRes.data);
      setPersons(personsRes.data);
      setError('');
    } catch (err) {
      setError('خطا در بارگذاری اطلاعات');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.card_number.trim() || !formData.person_id) {
      setError('شماره کارت و شخص الزامی است');
      return;
    }

    try {
      await addCard(formData);
      setSuccess('کارت با موفقیت اضافه شد');
      setFormData({ card_number: '', bank_name: '', person_id: '', description: '' });
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('خطا در افزودن کارت');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این کارت مطمئن هستید؟')) return;

    try {
      await deleteCard(id);
      setSuccess('کارت حذف شد');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('خطا در حذف کارت');
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center w-100"
        style={{ minHeight: '50vh' }}
      >
        <div className="spinner-border text-primary me-3" role="status" />
        <span className="fw-bold text-muted">در حال بارگذاری...</span>
      </div>
    );
  }

  return (
    <div className="d-grid gap-4">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setError('')}
          />
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setSuccess('')}
          />
        </div>
      )}

      <div className="row g-4">
        <div className="col-12 col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-transparent border-bottom-0 pb-0">
              <h2 className="h5 mb-1">افزودن کارت جدید</h2>
              <p className="text-muted small mb-0">اطلاعات کارت را تکمیل و ذخیره کنید</p>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} className="row g-3">
                <div className="col-12">
                  <label className="form-label">شماره کارت</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.card_number}
                    onChange={(e) => setFormData({ ...formData, card_number: e.target.value })}
                    placeholder="شماره ۱۶ رقمی کارت"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">نام بانک</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    placeholder="نام بانک صادرکننده"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">شخص</label>
                  <select
                    className="form-select"
                    value={formData.person_id}
                    onChange={(e) => setFormData({ ...formData, person_id: e.target.value })}
                  >
                    <option value="">انتخاب شخص</option>
                    {persons.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">توضیحات</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="توضیحات تکمیلی (اختیاری)"
                  />
                </div>
                <div className="col-12">
                  <button type="submit" className="btn btn-primary w-100">
                    ذخیره کارت
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-8">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center">
              <div>
                <h2 className="h5 mb-1">لیست کارت‌ها</h2>
                <p className="text-muted small mb-0">نمای کلی کارت‌های ثبت‌شده</p>
              </div>
              <span className="badge bg-primary text-white px-3 py-2">{cards.length} کارت فعال</span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th scope="col">ردیف</th>
                      <th scope="col">شماره کارت</th>
                      <th scope="col">بانک</th>
                      <th scope="col">شخص</th>
                      <th scope="col">توضیحات</th>
                      <th scope="col" className="text-end">عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cardsPagination.totalItems === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-4">
                          کارتی ثبت نشده است
                        </td>
                      </tr>
                    )}
                    {cardsPagination.paginatedItems.map((card, index) => (
                      <tr key={card.id}>
                        <td>
                          <span className="badge bg-secondary">{cardsPagination.rangeStart + index}</span>
                        </td>
                        <td className="fw-semibold text-dark">{card.card_number}</td>
                        <td>{card.bank_name}</td>
                        <td>{card.person_name}</td>
                        <td>{card.description || <span className="text-muted">بدون توضیح</span>}</td>
                        <td className="text-end">
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(card.id)}>
                            حذف
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderPaginationControls(cardsPagination)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Cards;

